import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import Toast from '../../../components/support/Toast';
import FeedbackSummaryCards from './components/FeedbackSummaryCards';
import NpsSummaryCards from './components/NpsSummaryCards';
import FeedbacksTable from './components/FeedbacksTable';
import NpsTable from './components/NpsTable';
import FeedbackDetailsDialog from './components/FeedbackDetailsDialog';
import ReleaseFixedBar from './components/ReleaseFixedBar';
import useAdminFeedbacks from './hooks/useAdminFeedbacks';

/**
 * AdminFeedbacks - Feedbacks e avaliações NPS enviados pelos usuários (Admin Geral)
 *
 * Responsabilidades:
 * - Consome useAdminFeedbacks() para estado de filtros, paginação e dados das duas abas
 * - Renderiza AreaTitle + abas "Feedbacks" / "Avaliações (NPS)", cada uma com cards de
 *   resumo, tabela filtrável e paginação server-side
 * - Orquestra o diálogo de detalhes (revisão do pedido / status do NPS), a faixa
 *   "Liberar corrigidos" e o Toast de feedback
 */
function AdminFeedbacks() {
  const {
    activeTab,
    setActiveTab,

    feedbacks,
    feedbacksSummary,
    feedbacksPagination,
    feedbacksLoading,
    feedbacksError,
    feedbackSearchInput,
    setFeedbackSearchInput,
    feedbackType,
    setFeedbackType,
    feedbackStatus,
    setFeedbackStatus,
    setFeedbackPage,
    refreshFeedbacks,

    nps,
    npsSummary,
    npsPagination,
    npsLoading,
    npsError,
    npsSearchInput,
    setNpsSearchInput,
    npsStatus,
    setNpsStatus,
    setNpsPage,
    refreshNps,
  } = useAdminFeedbacks();

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [detailsTarget, setDetailsTarget] = useState(null); // { type, id }

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleView = (type) => (id) => {
    setDetailsTarget({ type, id });
  };

  const handleCloseDetails = () => {
    setDetailsTarget(null);
  };

  const handleStatusSaved = (response) => {
    const notified = response?.notified;
    if (notified && (notified.bell || notified.email)) {
      showToast('Revisão salva e usuário avisado.', 'success');
    } else if (notified) {
      showToast('Revisão salva, mas o aviso não saiu. Salve de novo para tentar outra vez.', 'warning');
    } else {
      showToast(detailsTarget?.type === 'nps' ? 'Status atualizado com sucesso.' : 'Revisão salva com sucesso.', 'success');
    }
    if (detailsTarget?.type === 'nps') {
      refreshNps();
    } else {
      refreshFeedbacks();
    }
  };

  const handleReleased = (response) => {
    if (!response || response.success === false) {
      showToast(response?.error || 'Erro ao liberar os pedidos corrigidos.', 'error');
      return;
    }
    showToast(`${response.released} pedido(s) liberado(s), ${response.notified} usuário(s) avisado(s).`, 'success');
    refreshFeedbacks();
  };

  return (
    <Box sx={{ backgroundColor: '#F8F9FC', minHeight: '100vh', p: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 3 }}>
        <AreaTitle
          Title="Admin Geral · Feedbacks e NPS"
          Icon={<RateReviewOutlinedIcon />}
          Description="Revise os problemas e melhorias enviados pelos usuários, avise quem enviou quando estiver resolvido e acompanhe as avaliações NPS."
        />
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 3, borderBottom: '1px solid #E8ECF0' }}
      >
        <Tab value="feedbacks" label="Feedbacks" />
        <Tab value="nps" label="Avaliações (NPS)" />
      </Tabs>

      {activeTab === 'feedbacks' ? (
        <>
          <FeedbackSummaryCards summary={feedbacksSummary} />
          <ReleaseFixedBar count={feedbacksSummary.fixed} onDone={handleReleased} />
          <FeedbacksTable
            feedbacks={feedbacks}
            isLoading={feedbacksLoading}
            error={feedbacksError}
            pagination={feedbacksPagination}
            filters={{
              searchInput: feedbackSearchInput,
              onSearchChange: setFeedbackSearchInput,
              type: feedbackType,
              onTypeChange: setFeedbackType,
              status: feedbackStatus,
              onStatusChange: setFeedbackStatus,
            }}
            onPageChange={setFeedbackPage}
            onView={handleView('feedback')}
          />
        </>
      ) : (
        <>
          <NpsSummaryCards summary={npsSummary} />
          <NpsTable
            nps={nps}
            isLoading={npsLoading}
            error={npsError}
            pagination={npsPagination}
            filters={{
              searchInput: npsSearchInput,
              onSearchChange: setNpsSearchInput,
              status: npsStatus,
              onStatusChange: setNpsStatus,
            }}
            onPageChange={setNpsPage}
            onView={handleView('nps')}
          />
        </>
      )}

      <FeedbackDetailsDialog
        open={Boolean(detailsTarget)}
        type={detailsTarget?.type}
        id={detailsTarget?.id}
        onClose={handleCloseDetails}
        onStatusSaved={handleStatusSaved}
      />

      <Toast
        open={toast.open}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        message={toast.message}
        severity={toast.severity}
        autoHideDuration={5000}
      />
    </Box>
  );
}

export default AdminFeedbacks;
